import request from 'superagent';
import { STRINGS } from 'config';
import _debounce from 'lodash/debounce';
import { apiGetPostcode, apiGetAddresses, apiExternalGet, apiGet, apiDelete } from 'common/apiRequests';
import { getNextQuestion } from './index';
import { mockFindAddresses } from './mocks';

const consultation = STRINGS.CONSULTATION_REDUCER;

export const addMultipleSelectAnswer = answer => ({ type: consultation.SELECT_MULTIPLE_ANSWERS, payload: answer });

export const removeMultipleSelectAnswer = answer => ({ type: consultation.DESELECT_MULTIPLE_ANSWERS, payload: answer });

export const answerSingleChoice = answer => async dispatch => {
  await dispatch({ type: consultation.SELECT_SINGLE_ANSWER, payload: answer });
  dispatch(getNextQuestion());
};

export const updateDateAnswer = (date, dateUnit) => dispatch => {
  dispatch({ type: consultation.UPDATE_DATE_ANSWER(dateUnit), payload: date });
};

export const updateFreeTextAnswer = value => ({ type: consultation.UPDATE_FREETEXT_ANSWER, payload: value });

export const validatePostcode = postcode => (dispatch, getState) => {
  dispatch({ type: consultation.VALIDATE_POSTCODE_REQUEST, payload: postcode });
  return apiGetPostcode(getState().consultationReducer.practice.practiceId, postcode)
    .then(response => {
      if (response.body) {
        dispatch({ type: consultation.VALIDATE_POSTCODE_SUCCESS, payload: response.body.result });
        return response.body.result;
      }
      return dispatch({ type: consultation.VALIDATE_POSTCODE_ERROR, payload: response });
    })
    .catch(error => dispatch({ type: consultation.VALIDATE_POSTCODE_ERROR, payload: error }));
};

export const findAddresses = postcode => (dispatch, getState) => {
  const findAddressesUrl = STRINGS.API.ADDRESSES_URL(getState().consultationReducer.practice.practiceId, postcode);
  if (!findAddressesUrl) {
    return dispatch(mockFindAddresses(postcode));
  }
  dispatch({ type: consultation.FIND_ADDRESSES_REQUEST, payload: postcode });
  return apiGetAddresses(getState().consultationReducer.practice.practiceId, postcode)
    .then(response => {
      if (response.body) {
        dispatch({ type: consultation.FIND_ADDRESSES_SUCCESS, payload: response.body.addresses });
      } else {
        dispatch({ type: consultation.FIND_ADDRESSES_ERROR, payload: response });
      }
    })
    .catch(error => dispatch({ type: consultation.FIND_ADDRESSES_ERROR, payload: error }));
};

export const clearAddresses = () => ({ type: consultation.CLEAR_ADDRESSES });

/*
 setDateAnswerId needed to prevent consecutive date answers mistakenly using the same date redux state.
*/
export const setDateAnswerId = answerId => ({ type: consultation.SET_DATE_ANSWER_ID, payload: answerId });

export const updateCompoundAnswers = compoundAnswers => ({ type: consultation.UPDATE_COMPOUND_ANSWERS, payload: compoundAnswers });

export const updateBloodPressureReading = newBPState => ({ type: consultation.UPDATE_BP_READING, payload: newBPState });

export const removeBloodPressureReading = id => ({ type: consultation.REMOVE_BP_READING, payload: id });

export const addBloodPressureReading = newReading => ({ type: consultation.ADD_BP_READING, payload: newReading });

export const updateBMIWeight = weightAndMeasure => ({ type: consultation.UPDATE_BMI_WEIGHT, payload: weightAndMeasure });

export const updateBMIHeight = heightAndMeasure => ({ type: consultation.UPDATE_BMI_HEIGHT, payload: heightAndMeasure });

export const toggleBMIWeightMeasure = convertedWeightAndMeasure => ({ type: consultation.TOGGLE_BMI_WEIGHT, payload: convertedWeightAndMeasure });

export const toggleBMIHeightMeasure = convertedHeightAndMeasure => ({ type: consultation.TOGGLE_BMI_HEIGHT, payload: convertedHeightAndMeasure });

export const addPhoto = photo => ({ type: consultation.ADD_PHOTO, payload: photo });

export const deletePhoto = photoId => ({ type: consultation.DELETE_PHOTO, payload: photoId });

export const updatePhotoDescription = (id, fileDescription) => ({ type: consultation.UPDATE_PHOTO_DESCRIPTION, payload: { id, fileDescription } });

export const getTypeAheadSearch = searchTerm => (dispatch, getState) => {
  dispatch({ type: consultation.GET_TYPE_AHEAD_SEARCH_REQUEST, payload: searchTerm });
  if (!searchTerm) {
    return dispatch({ type: consultation.GET_TYPE_AHEAD_SEARCH_SUCCESS, payload: { results: [] } });
  }
  return apiExternalGet(`${getState().consultationReducer.question.searchUrl}${searchTerm}`, getState().consultationReducer.practice.practiceId)
    .then(response => dispatch({ type: consultation.GET_TYPE_AHEAD_SEARCH_SUCCESS, payload: { ...response.body, results: response.body.results || [] } }))
    .catch(error => dispatch({ type: consultation.GET_TYPE_AHEAD_SEARCH_ERROR, payload: error }));
};

const debounceGetTypeAheadSearch = _debounce((arg, dispatch) => dispatch(getTypeAheadSearch(arg)), 500);
const debouncedGetTypeAheadSearch = arg => dispatch => debounceGetTypeAheadSearch(arg, dispatch);

export const updateSearchTerm = searchTerm => dispatch => {
  dispatch({ type: consultation.UPDATE_SEARCH_TERM, payload: searchTerm });
  dispatch(debouncedGetTypeAheadSearch(searchTerm));
};

export const selectSearchResult = (result, displayName) => ({ type: consultation.SELECT_SEARCH_RESULT, payload: { result, displayName } });

const blobToBase64 = async blob => new Promise(resolve => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.readAsDataURL(blob);
});

export const uploadPhotoToS3 = (file, photoId, placeholderPhoto) => async (dispatch, getState) => {
  const { sessionId, practice: { practiceId } } = getState().consultationReducer;
  dispatch({ type: consultation.UPLOAD_PHOTO_TO_S3_REQUEST, payload: { ...placeholderPhoto, photoId } });
  try {
    const fileType = file.type.split(/[/.]/)[1];
    const getPresignUrlResponse = await apiGet(`/s3Image/metadata/uploadUrl/${sessionId}/${fileType}`, practiceId);
    const { presignedUrl, fileName } = getPresignUrlResponse.body;
    await request
      .put(presignedUrl)
      .send(file);
    const getImageFromS3Response = await request
      .get(presignedUrl)
      .query({ height: 100, width: 100, fit: 'inside' })
      .responseType('blob');
    const imageAsBase64String = await blobToBase64(getImageFromS3Response.body);
    dispatch({
      type: consultation.UPLOAD_PHOTO_TO_S3_SUCCESS,
      payload: { fileBytes: imageAsBase64String, fileDescription: '', fileName: fileName, photoId },
    });
  } catch {
    dispatch({
      type: consultation.UPLOAD_PHOTO_TO_S3_ERROR,
      payload: { photoId, errorMessage: 'There was a problem uploading your image, please try again.' },
    });
  }
};

export const deletePhotoFromS3 = (photoId, photo) => (dispatch, getState) => {
  const { sessionId, practice: { practiceId } } = getState().consultationReducer;
  dispatch({ type: consultation.DELETE_PHOTO_FROM_S3_REQUEST, payload: photoId });
  return apiDelete(`/s3Image/${sessionId}/${photo.fileName}`, practiceId)
    .then(() => dispatch({ type: consultation.DELETE_PHOTO_FROM_S3_SUCCESS, payload: photoId }))
    .catch(() => (
      dispatch({
        type: consultation.DELETE_PHOTO_FROM_S3_ERROR,
        payload: { photoId, errorMessage: 'There was a problem deleting your image, please try again.' },
      })
    ));
};
